import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy, inject } from '@angular/core';

@Directive({
  selector: '[appTabStrip]',
  standalone: true,
})
export class TabStripDirective implements AfterViewInit, OnDestroy {
  private static readonly EDGE_PADDING = 24;

  private static readonly PAGE_FRACTION = 0.8;

  private static readonly DRAG_THRESHOLD = 5;

  private static instances = 0;

  @Input('appTabStrip') label = '';

  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);

  private readonly uid = `olv-tabs-${TabStripDirective.instances++}`;

  private mutations?: MutationObserver;
  private panelMutations?: MutationObserver;
  private observedPanel?: HTMLElement;
  private lastActive: Element | null = null;

  private wrapper?: HTMLElement;
  private scrollStartBtn?: HTMLButtonElement;
  private scrollEndBtn?: HTMLButtonElement;

  private sizeObserver?: ResizeObserver;
  private panning = false;

  private pointerDown = false;
  private dragging = false;
  private dragStartX = 0;
  private dragStartScroll = 0;
  private suppressNextClick = false;

  ngAfterViewInit(): void {
    const el = this.hostRef.nativeElement;
    this.buildChrome(el);

    this.zone.runOutsideAngular(() => {
      el.addEventListener('keydown', this.onKeydown);

      this.mutations = new MutationObserver(() => {
        this.refreshOverflowState();
        this.revealActiveTab();
        this.syncAria();
      });
      this.mutations.observe(el, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });

      el.addEventListener('scroll', this.refreshOverflowState, { passive: true });
      window.addEventListener('resize', this.refreshOverflowState, { passive: true });

      this.sizeObserver = new ResizeObserver(this.refreshOverflowState);
      this.sizeObserver.observe(el);

      this.refreshOverflowState();
      this.revealActiveTab('auto');
    });

    this.syncAria();
  }

  ngOnDestroy(): void {
    const el = this.hostRef.nativeElement;
    this.setPanning(false);
    this.sizeObserver?.disconnect();
    el.removeEventListener('scroll', this.refreshOverflowState);
    window.removeEventListener('resize', this.refreshOverflowState);
    el.removeEventListener('keydown', this.onKeydown);
    this.mutations?.disconnect();
    this.panelMutations?.disconnect();

    const wrapper = this.wrapper;
    if (wrapper?.parentNode) {
      wrapper.parentNode.insertBefore(el, wrapper);
      wrapper.remove();
    }
  }

  private setPanning(on: boolean): void {
    if (on === this.panning) return;
    this.panning = on;

    const el = this.hostRef.nativeElement;

    if (on) {
      this.zone.runOutsideAngular(() => {
        el.addEventListener('pointerdown', this.onPointerDown);
        el.addEventListener('pointermove', this.onPointerMove);
        el.addEventListener('pointerup', this.onPointerUp);
        el.addEventListener('pointercancel', this.onPointerUp);
        el.addEventListener('click', this.onClickCapture, { capture: true });
      });

      this.lastActive = null;
      return;
    }

    el.removeEventListener('pointerdown', this.onPointerDown);
    el.removeEventListener('pointermove', this.onPointerMove);
    el.removeEventListener('pointerup', this.onPointerUp);
    el.removeEventListener('pointercancel', this.onPointerUp);
    el.removeEventListener('click', this.onClickCapture, { capture: true } as EventListenerOptions);

    this.pointerDown = false;
    this.dragging = false;
    this.suppressNextClick = false;
    el.classList.remove('is-dragging');
  }

  private buildChrome(el: HTMLElement): void {
    const parent = el.parentNode;
    if (!parent) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'tab-strip';
    parent.insertBefore(wrapper, el);
    wrapper.appendChild(el);

    this.scrollStartBtn = this.buildScrollButton('start', 'Scroll tabs left');
    this.scrollEndBtn = this.buildScrollButton('end', 'Scroll tabs right');
    wrapper.appendChild(this.scrollStartBtn);
    wrapper.appendChild(this.scrollEndBtn);

    this.wrapper = wrapper;
  }

  private buildScrollButton(edge: 'start' | 'end', label: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `tab-strip-scroll tab-strip-scroll--${edge}`;
    btn.setAttribute('aria-label', label);
    btn.setAttribute('aria-hidden', 'true');
    btn.tabIndex = -1;
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      (edge === 'start' ? '<path d="m15 18-6-6 6-6"/>' : '<path d="m9 18 6-6-6-6"/>') +
      '</svg>';
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.scrollByPage(edge === 'start' ? -1 : 1);
    });
    return btn;
  }

  private scrollByPage(direction: 1 | -1): void {
    const el = this.hostRef.nativeElement;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({
      left: direction * el.clientWidth * TabStripDirective.PAGE_FRACTION,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const el = this.hostRef.nativeElement;
    if (el.scrollWidth <= el.clientWidth + 1) return;

    this.pointerDown = true;
    this.dragging = false;
    this.suppressNextClick = false;
    this.dragStartX = event.clientX;
    this.dragStartScroll = el.scrollLeft;
  };

  private readonly onClickCapture = (event: MouseEvent): void => {
    if (!this.suppressNextClick) return;
    this.suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.pointerDown) return;

    const el = this.hostRef.nativeElement;
    const dx = event.clientX - this.dragStartX;

    if (!this.dragging) {
      if (Math.abs(dx) < TabStripDirective.DRAG_THRESHOLD) return;
      this.dragging = true;
      el.setPointerCapture?.(event.pointerId);
      el.classList.add('is-dragging');
    }

    event.preventDefault();
    el.scrollLeft = this.dragStartScroll - dx;
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (!this.pointerDown) return;
    this.pointerDown = false;

    if (!this.dragging) return;
    this.dragging = false;

    const el = this.hostRef.nativeElement;
    el.releasePointerCapture?.(event.pointerId);
    el.classList.remove('is-dragging');
    this.suppressNextClick = true;
  };

  private get tabs(): HTMLElement[] {
    return Array.from(this.hostRef.nativeElement.querySelectorAll<HTMLElement>('.tab'));
  }

  private findPanel(): HTMLElement | null {
    let node: Element | null = (this.wrapper ?? this.hostRef.nativeElement).nextElementSibling;
    while (node) {
      if (node.classList.contains('tab-content')) return node as HTMLElement;
      node = node.nextElementSibling;
    }
    return null;
  }

  private syncAria(): void {
    const el = this.hostRef.nativeElement;
    el.setAttribute('role', 'tablist');
    if (this.label) el.setAttribute('aria-label', this.label);

    const tabs = this.tabs;
    const panel = this.findPanel();
    const panelId = panel?.id || `${this.uid}-panel`;
    let activeTabId = '';

    tabs.forEach((tab, index) => {
      const selected = tab.classList.contains('active');

      if (!tab.id) tab.id = `${this.uid}-tab-${index}`;
      if (selected) activeTabId = tab.id;

      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;

      if (selected && panel) {
        tab.setAttribute('aria-controls', panelId);
      } else {
        tab.removeAttribute('aria-controls');
      }
    });

    if (!panel) return;
    if (!panel.id) panel.id = panelId;
    panel.setAttribute('role', 'tabpanel');
    if (activeTabId) panel.setAttribute('aria-labelledby', activeTabId);

    if (panel !== this.observedPanel) {
      this.panelMutations?.disconnect();
      this.observedPanel = panel;
      this.zone.runOutsideAngular(() => {
        this.panelMutations = new MutationObserver(() => this.syncPanelFocusability(panel));
        this.panelMutations.observe(panel, { childList: true, subtree: true });
      });
    }
    this.syncPanelFocusability(panel);
  }

  private syncPanelFocusability(panel: HTMLElement): void {
    const focusable = panel.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable) {
      panel.removeAttribute('tabindex');
    } else {
      panel.tabIndex = 0;
    }
  }

  private readonly onKeydown = (event: KeyboardEvent): void => {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const tabs = this.tabs;
    if (!tabs.length) return;

    const current = tabs.indexOf(document.activeElement as HTMLElement);
    if (current === -1) return;

    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      default:
        next = tabs.length - 1;
    }

    event.preventDefault();
    tabs.forEach((tab, i) => (tab.tabIndex = i === next ? 0 : -1));
    tabs[next].focus();
    this.scrollTabIntoView(tabs[next]);
  };

  private readonly refreshOverflowState = (): void => {
    const el = this.hostRef.nativeElement;
    const maxScroll = el.scrollWidth - el.clientWidth;

    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft >= maxScroll - 1;
    const state = maxScroll <= 1 ? 'none' : atStart ? 'end' : atEnd ? 'start' : 'both';

    el.dataset['overflow'] = state;
    if (this.wrapper) this.wrapper.dataset['overflow'] = state;

    this.setPanning(state !== 'none');
  };

  private revealActiveTab(behavior: ScrollBehavior = 'smooth'): void {
    const active = this.hostRef.nativeElement.querySelector<HTMLElement>('.tab.active');
    if (!active || active === this.lastActive) return;
    this.lastActive = active;
    this.scrollTabIntoView(active, behavior);
  }

  private scrollTabIntoView(target: HTMLElement, behavior: ScrollBehavior = 'smooth'): void {
    const el = this.hostRef.nativeElement;
    if (el.scrollWidth <= el.clientWidth + 1) return;

    const strip = el.getBoundingClientRect();
    const tab = target.getBoundingClientRect();
    const pad = TabStripDirective.EDGE_PADDING;

    let delta = 0;
    if (tab.left < strip.left + pad) {
      delta = tab.left - strip.left - pad;
    } else if (tab.right > strip.right - pad) {
      delta = tab.right - strip.right + pad;
    }
    if (delta === 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: delta, behavior: reduceMotion ? 'auto' : behavior });
  }
}
